import {ChangeDetectionStrategy, Component, computed, input, output, signal} from '@angular/core';
import {TranslatePipe} from '@brunata-angular/util';
import {
    ExportFileFormat,
    ExportFileFormatForm,
    FileFormatCapabilities,
    FileFormatSettingsTab,
    isDefaultExportFileFormat,
    visibleFileFormatTabs,
} from '../../shared/models/export-file-format.model';
import {CharactersTabComponent} from './characters-tab.component';
import {DatesTabComponent} from './dates-tab.component';
import {NumbersTabComponent} from './numbers-tab.component';

@Component({
    selector: 'app-file-format-settings',
    standalone: true,
    imports: [TranslatePipe, CharactersTabComponent, DatesTabComponent, NumbersTabComponent],
    template: `
        @if (visibleTabs().length > 0) {
            <button
                type="button"
                class="settings-band w-100 d-flex align-items-center gap-3 px-3 py-3 border-top text-start"
                data-testid="advanced-toggle"
                [attr.aria-expanded]="advancedOpen()"
                (click)="toggleAdvanced()">
                <span class="fw-bold">
                    {{
                        'customizedexports_export_fileformat_settings_label'
                            | translate: 'online' : 'export'
                    }}
                </span>
                <i
                    class="bi flex-shrink-0 ms-auto"
                    [class.bi-chevron-down]="!advancedOpen()"
                    [class.bi-chevron-up]="advancedOpen()"></i>
            </button>

            @if (advancedOpen()) {
                <div class="advanced-settings border-top">
                    <ul class="nav nav-tabs" role="tablist">
                        @for (tab of visibleTabs(); track tab) {
                            <li class="nav-item">
                                <button
                                    type="button"
                                    role="tab"
                                    class="nav-link"
                                    [class.active]="tab === activeTab()"
                                    [attr.aria-selected]="tab === activeTab()"
                                    (click)="showTab(tab)">
                                    {{ tabLabelKeys[tab] | translate: 'online' : 'export' }}
                                </button>
                            </li>
                        }
                    </ul>

                    <div class="tab-body px-4 py-2">
                        @switch (activeTab()) {
                            @case ('characters') {
                                <app-characters-tab [form]="form()" />
                            }
                            @case ('numbers') {
                                <app-numbers-tab [form]="form()" [settings]="settings()" />
                            }
                            @case ('dates') {
                                <app-dates-tab
                                    [form]="form()"
                                    [settings]="settings()"
                                    [carriesTimestamps]="capabilities().timestamps" />
                            }
                        }
                    </div>

                    @if (!isAtDefaults()) {
                        <div class="d-flex justify-content-end px-4 pb-3">
                            <button
                                type="button"
                                class="btn btn-link btn-sm p-0"
                                data-testid="reset-defaults"
                                (click)="settingsReset.emit()">
                                {{
                                    'customizedexports_export_fileformat_reset_defaults'
                                        | translate: 'online' : 'export'
                                }}
                            </button>
                        </div>
                    }
                </div>
            }
        }
    `,
    styles: `
        :host {
            display: block;
        }

        .advanced-settings {
            --bs-nav-tabs-border-color: var(--bs-border-color);
        }

        .settings-band {
            background: none;
            border: 0;
            border-top: 1px solid var(--bs-border-color);
            color: inherit;
        }

        .settings-band .bi {
            color: var(--bs-primary);
        }

        .settings-band:hover {
            background-color: var(--brunata-blue-faint);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileFormatSettingsComponent {
    readonly form = input.required<ExportFileFormatForm>();
    readonly settings = input.required<ExportFileFormat>();
    readonly capabilities = input.required<FileFormatCapabilities>();

    readonly settingsReset = output<void>();

    protected readonly tabLabelKeys: Record<FileFormatSettingsTab, string> = {
        characters: 'customizedexports_export_fileformat_section_characters',
        numbers: 'customizedexports_export_fileformat_section_numbers',
        dates: 'customizedexports_export_fileformat_section_dates',
    };

    private readonly requestedTab = signal<FileFormatSettingsTab>('characters');

    protected readonly advancedOpen = signal(false);

    protected readonly visibleTabs = computed(() => visibleFileFormatTabs(this.capabilities()));

    protected readonly activeTab = computed<FileFormatSettingsTab | null>(() => {
        const tabs = this.visibleTabs();
        const requested = this.requestedTab();
        return tabs.includes(requested) ? requested : (tabs[0] ?? null);
    });

    protected readonly isAtDefaults = computed(() => isDefaultExportFileFormat(this.settings()));

    protected toggleAdvanced(): void {
        this.advancedOpen.update(open => !open);
    }

    protected showTab(tab: FileFormatSettingsTab): void {
        this.requestedTab.set(tab);
    }
}
